import React, { useEffect, useState } from 'react';
import {
  Button, Card, CardBody, CardText, CardTitle, Container,
  DropdownItem, DropdownMenu, DropdownToggle, UncontrolledButtonDropdown
} from 'reactstrap';
import { eFormLinkService } from '../../services';
import { setAlertNotification } from '../../actions/util';
import EFormLinkModal from './EFormLinkModal';

const EFormLinkList = () => {
  const [links, setLinks] = useState([]);
  const [selectedLink, setSelectedLink] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const roles = sessionStorage.getItem('rolesLFS') || '';
  const isAdmin = roles.split(',').some(role => role.trim() === 'Admin');

  const getLinks = () => {
    eFormLinkService().getAll().then(res => {
      if (res && Array.isArray(res.data)) {
        setLinks(res.data);
      } else {
        setLinks([]);
        setAlertNotification('Unable to load eForm links.', 'danger');
      }
    });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    getLinks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openModal = (link = {}) => {
    setSelectedLink(link);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (!isSaving) {
      setIsModalOpen(false);
      setSelectedLink(null);
    }
  };

  const saveLink = (values) => {
    setIsSaving(true);
    const request = selectedLink?.id
      ? eFormLinkService().update(selectedLink.id, values)
      : eFormLinkService().add(values);

    request
      .then(() => {
        setAlertNotification(selectedLink?.id ? 'eForm link updated.' : 'eForm link added.');
        setIsModalOpen(false);
        setSelectedLink(null);
        getLinks();
      })
      .catch(err => {
        setAlertNotification(err?.response?.data?.message || 'Unable to save the eForm link.', 'danger');
      })
      .then(() => setIsSaving(false));
  };

  const deleteLink = (id) => {
    eFormLinkService().delete(id)
      .then(() => {
        setAlertNotification('eForm link removed.');
        getLinks();
      })
      .catch(err => {
        setAlertNotification(err?.response?.data?.message || 'Unable to remove the eForm link.', 'danger');
      });
  };

  const copyLink = async (url) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const input = document.createElement('textarea');
        input.value = url;
        input.style.position = 'fixed';
        input.style.opacity = '0';
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
      }
      setAlertNotification('Link copied to clipboard.');
    } catch (err) {
      setAlertNotification('Unable to copy the link.', 'danger');
    }
  };

  return (
    <Container fluid>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>eForms</h3>
        {isAdmin && <Button outline color="secondary" onClick={() => openModal()}>Add eForm</Button>}
      </div>

      {links.length ? links.map(link => (
        <Card key={link.id} className="mb-3">
          <CardBody>
            <div className="d-flex flex-wrap justify-content-between align-items-start">
              <div className="pr-3" style={{ minWidth: 0 }}>
                <CardTitle tag="h5">{link.title}</CardTitle>
                {link.description && <CardText>{link.description}</CardText>}
                <a href={link.url} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>
                  {link.url}
                </a>
              </div>

              {isAdmin && (
                <div className="d-flex flex-shrink-0 mt-2 mt-md-0">
                  <Button size="sm" outline color="secondary" className="mr-2" onClick={() => openModal(link)}>
                    Edit
                  </Button>
                  <UncontrolledButtonDropdown>
                    <DropdownToggle size="sm" outline color="danger" caret>Delete</DropdownToggle>
                    <DropdownMenu right style={{ minWidth: '8rem' }}>
                      <DropdownItem className="text-danger" onClick={() => deleteLink(link.id)}>Confirm</DropdownItem>
                    </DropdownMenu>
                  </UncontrolledButtonDropdown>
                </div>
              )}
            </div>

            <Button size="sm" outline color="secondary" className="mt-3" onClick={() => copyLink(link.url)}>
              Copy Link
            </Button>
          </CardBody>
        </Card>
      )) : (
        <div className="text-center text-muted border rounded p-4">No eForm links are available.</div>
      )}

      {isAdmin && isModalOpen && (
        <EFormLinkModal
          isOpen={isModalOpen}
          link={selectedLink}
          isSaving={isSaving}
          onSave={saveLink}
          onClose={closeModal}
        />
      )}
    </Container>
  );
};

export default EFormLinkList;
